// Copy of the "Project costs" walkthrough. The rules every text follows are in ../../de/help.js.
export default {
  title: "Project costs",
  // Short names of the views, for the header, the timeline and "Continue to …".
  section: {
    ProjectCosts: "All projects",
    ProjectCostsForOne: "Single project",
    HourlyWageReportCosts: "[[Hwr]]",
    ConstructionReportCosts: "[[Cr]]",
    // The related views, in the order they are played.
    UserPositionDetail: "Employee position",
    MaterialDetail: "Material",
    SettingsOverview: "Settings",
    EmployeeDetail: "Employee",
    ProjectDetail: "Project",
  },
  // Heading of the last chapter, in front of each view's name.
  related: "Connections",
  step: {
    // ── All projects ───────────────────────────────────────────────────────
    intro: {
      text: "The running post-calculation shows what your projects have cost so far. It adds up the reports as they come in and sets the costs against the budget.",
    },
    projects: {
      text: "This is where you choose which projects the overview adds up. On a first visit every one is selected.",
    },
    range: { text: "From and To bound the period. Every report dated inside it counts." },
    presets: {
      text: "The quick ranges set the period to 30 days, 90 days, the year or the month. Every figure on the page is worked out again.",
    },
    valuation: {
      text: "Cost shows what the work costs the company. Revenue shows what it is worth at the project's selling prices.",
    },
    modes: {
      text: "Four views on the same figures. Heatmap by day, Trend as a running total, Week by calendar week, Analysis by cost type.",
    },
    health: {
      text: "The headline says how the company stands as a whole. It measures budget use against completion and counts the projects per status.",
    },
    combined: { text: "Expanded, this panel adds up every selected project in the view you are on." },
    heatmap: {
      text: "One row per project, one column per day. The darker a day, the more cost is booked on it.",
    },
    "heatmap-row": {
      text: "In front of the project's name, a mark shows whether budget use matches progress. When it runs ahead of completion, it turns yellow or red.",
    },
    "heatmap-total": {
      text: "On the right stands the period's total. The arrow beside it expands the project's trend.",
    },
    day: { text: "Clicking a day shows which cost types it is made of and which reports are behind it." },
    "show-cr": {
      text: "The two switches say which reports count. [[Cr.pl]] carry a cost of their own, since the same work may also sit in [[hwr:a/an]] [[hwr]].",
    },
    chart: {
      text: "The trend adds the cost up across the period. It shows how quickly the budget is being used up.",
    },
    week: {
      text: "The week view adds up each calendar week by cost type. Clicking a week shows its reports.",
    },
    analysis: {
      text: "The analysis shows the share of each cost type, the ranking of the projects and the most expensive reports.",
    },
    notice: {
      text: "The figures use the rates and prices set today. Earlier settlements may differ from them.",
    },

    // ── One project ────────────────────────────────────────────────────────
    single: {
      text: "The same page for a single project. It opens on the planned installation period.",
    },
    budget: {
      text: "The budget is a ceiling for the project's cost. The status measures the share used against completion, which comes from the service catalogue or is typed in here. Quiet months therefore do no harm.",
    },
    plan: {
      text: "The running calculation compares the contract value with the cost so far. The expected profit follows from it.",
    },
    manual: {
      text: "Cost and revenue without a report are typed in here. They count towards the cost so far and the margin.",
    },
    rentals: {
      text: "Rentals are included in the machine costs, days without usage by the supplier's terms. On those days the reports show the rented device as “included in rental”.",
    },
    services: {
      text: "Completion and earned value come from the service catalogue. The Services view shows cost and contribution margin per position.",
    },
    "week-reports": {
      text: "Behind every week sit its reports. One click opens the cost statement of a single report.",
    },

    // ── Hourly-wage report ─────────────────────────────────────────────────
    "hwr-head": {
      text: "The cost statement of one [[hwr]]. Its number, its day and its project stand at the top.",
    },
    "hwr-summary": { text: "The bar adds the report up by labour, machines, material and additional services." },
    "hwr-labor": {
      text: "Every hour is costed at its position's hourly rate. The cost column also carries the employer's contribution.",
    },
    "hwr-machines": {
      text: "Machines cost amount times hours times purchase price. A day rate counts every started day of use.",
    },
    "hwr-materials": { text: "Material and additional services cost their amount times their purchase price." },
    "hwr-print": { text: "Print gives you the same statement as a PDF, with the same figures as here." },
    "hwr-open": {
      text: "This opens the [[hwr]] itself, in the same panel as on the project. After a save the page works its figures out again.",
    },

    // ── Construction report ────────────────────────────────────────────────
    "cr-summary": {
      text: "For [[cr:a/an]] [[cr]] the bar adds up labour, machines and material. It has no additional services.",
    },
    "cr-shift": { text: "Every shift brings its working time, its break and its own subtotal." },
    "cr-labor": {
      text: "A labour line costs the shift's working time times the hourly rate times the number of people. The employer's contribution is added.",
    },
    "cr-machines": {
      text: "An hour-rate device counts the work time of its shifts. A day-rate device counts each day of use, past midnight a second one.",
    },
    again: { text: "This button is on every page that has an explanation. One click starts it again." },

    // ── Setup ──────────────────────────────────────────────────────────────
    "position-rate": {
      text: "Every hour is costed at the position's hourly rate. The project's rate, pre-filled from the customer, is the revenue rate, and only counts for costs when none is set here.",
    },
    "material-price": {
      text: 'Every amount built in is costed at the purchase price picked and fixed when it was booked. A price changed later reaches existing bookings only after "Re-price" under that price in the project. The selling price is set on the project, pre-filled from the customer\'s prices.',
    },
    "settings-contribution": {
      text: "The employer's contribution from the settings is added to every hour of labour. That is why a project's labour cost is a full cost.",
    },
    "employee-tabs": { text: "The Company relevant tab holds everything to do with this person's pay." },
    "employee-contribution": {
      text: "A rate set for this person replaces the one from the settings. Only their hours are costed with it.",
    },
    "hwr-register-costs": {
      text: "The cost statement also opens from the project's list of [[hwr.pl]]. Every row has its own button for it.",
    },
    "cr-register-costs": {
      text: "The same button sits in every row of the [[cr.pl]]. It opens that report's cost statement.",
    },
  },
}
